/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 03/12/21
 */

import { isNullish } from '@/wpt-lib/utils/isNullish';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import config from '@/config';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { User } from '@/wpt-lib/interfaces/User';

export interface LoginError {
    statusCode: number;
    message: string;
    timestamp: string;
    path: string;
    stack?: string;
}

export const isLoginError = (err: unknown): err is LoginError => {
    return (
        typeof err === 'object' &&
        !isNullish(err) &&
        'statusCode' in err &&
        'message' in err &&
        'timestamp' in err &&
        'path' in err
    );
};

interface TokenClaims {
    exp: number;
    iat: number;
    samlProfile: unknown;
    user: User;
}

export interface AuthState {
    isAuthenticated: boolean;
    accessToken?: string;
    claims?: TokenClaims;
    error: string | LoginError | undefined | unknown;
}

interface IAuthContext {
    authState: AuthState;

    login(): void;
    logout(): void;
    setAccessToken(newAccessToken: string): void;
    setError(error: unknown | undefined): void;
}

export const AuthContext = createUnprovisionedContext<IAuthContext>('AuthContext');

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [accessToken, setAccessTokenState] = useState<AuthState['accessToken']>(
        localStorage.getItem(config.auth.accessTokenStorageKey) || undefined,
    );
    const [loginError, setError] = useState<LoginError | unknown | undefined>();

    const decodeAccessTokenClaims = (token: string): [claims?: TokenClaims, error?: string] => {
        try {
            const split = token.split('.');
            const claims = JSON.parse(atob(split[1]));
            return [claims, undefined];
        } catch (e: unknown) {
            console.error('JWT decoding error:', e);
            return [undefined, `${e}`];
        }
    };

    const setAccessToken = (newAccessToken: string | undefined) => {
        setAccessTokenState(newAccessToken);
        setError(undefined);

        if (newAccessToken) {
            localStorage.setItem(config.auth.accessTokenStorageKey, newAccessToken);
        } else {
            localStorage.removeItem(config.auth.accessTokenStorageKey);
        }
    };

    const getAuthState = (): AuthState => {
        let error: string | undefined;
        let isAuthenticated = true;
        if (!accessToken) {
            return {
                isAuthenticated: false,
                error: loginError,
            };
        }

        const [claims, claimsError] = decodeAccessTokenClaims(accessToken);

        const threeMinutes = 3 * 60;
        if (claimsError || !claims) {
            error = `Invalid token format: ${claimsError}`;
            isAuthenticated = false;
        } else if (claims.iat > new Date().getTime() / 1000 + threeMinutes) {
            error = 'Token issued in the future';
            isAuthenticated = false;
        } else if (new Date().getTime() / 1000 > claims.exp) {
            error = 'Token expired';
            isAuthenticated = false;
        }

        return {
            isAuthenticated,
            accessToken,
            claims,
            error,
        };
    };

    const authState = useMemo(getAuthState, [accessToken, loginError]);

    const context: IAuthContext = {
        authState,
        login() {
            setAccessToken(undefined);
            window.location.href = config.auth.loginUrl;
        },
        logout() {
            setAccessToken(undefined);
        },
        setAccessToken(newAccessToken: string) {
            setAccessToken(newAccessToken);
        },
        setError,
    };

    return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
